/**
 * TableObjectStepTwoDirective class defines a directive for handling the second step of a table object in an AngularJS application.
 * This directive is restricted to be used as an attribute, replaces the element, and requires the 'asfTableObject' controller.
 */
class TableObjectStepTwoDirective {
    /**
     * Constructor for TableObjectStepTwoDirective.
     * @param {Object} $translate - AngularJS $translate service for internationalization.
     */
    constructor($translate) {
        this.$translate = $translate;
    }

    /**
     * Static method to define and return the directive configuration object.
     * @param {Object} $translate - AngularJS $translate service for internationalization.
     * @returns {Object} Directive configuration object.
     */
    static directiveFactory($translate) {
        return new TableObjectStepTwoDirective($translate).directive();
    }

    /**
     * Method to define and return the directive configuration object.
     * @returns {Object} Directive configuration object.
     */
    directive() {
        return {
            restrict: 'A',
            replace: true,
            require: '^asfTableObject',
            templateUrl: '/views/ib/contentobjects/table/step2.html',
            link: (scope, element, attributes, controller) => {
                /**
                 * Helper function to generate a range of numbers.
                 * @param {number} range - The number of elements in the range.
                 * @returns {Array} An array of numbers from 0 to range - 1.
                 */
                const _range = (range) => {
                    const array = [];
                    for (let i = 0; i < range; i++) {
                        array.push(i);
                    }
                    return array;
                };

                scope.tableMaxRows = controller.maxRows;
                scope.tableMaxCols = controller.maxCols;

                scope.tableMinRows = controller.minRows;
                scope.tableMinCols = controller.minCols;

                scope.containerWidth = 280;
                scope.containerHeight = 180;

                scope.rowsHeight = scope.containerHeight / scope.tableMaxRows;
                const colsWidth = scope.containerWidth / scope.tableMaxCols;
                const colsHeight = scope.containerHeight / scope.tableMaxRows;
                scope.colsStyle = {
                    width: `${colsWidth}px`,
                    height: `${colsHeight}px`
                };

                scope.gridSizeTranslateValues = {
                    rows: 0,
                    cols: 0
                };
                scope.activeCols = {};
                scope.rowsRange = _range(scope.tableMaxRows);
                scope.colsRange = _range(scope.tableMaxCols);

                /**
                 * Handles the next step action.
                 * @param {number} row - The selected row.
                 * @param {number} col - The selected column.
                 */
                scope.onNextStep = (row, col) => {
                    controller.setStepData(2, {
                        rows: row,
                        cols: col
                    });
                    if (!controller.getTableData()) {
                        controller.setTableData([]);
                    }
                    controller.saveTableObject();
                    controller.nextStep();
                };

                /**
                 * Handles the mouse over event on a cell.
                 * @param {number} row - The row index of the cell.
                 * @param {number} col - The column index of the cell.
                 */
                scope.onMouseOverCell = (row, col) => {
                    scope.rowsRange.forEach((parentIndex) => {
                        scope.colsRange.forEach((index) => {
                            scope.activeCols[`${parentIndex}-${index}`] = {
                                active: parentIndex <= row && index <= col,
                                current: parentIndex === row && index === col
                            };
                        });
                    });
                    scope.gridSizeTranslateValues = {
                        rows: row + 1,
                        cols: col + 1
                    };
                };
            }
        };
    }
}

/**
 * Dependency injection annotation for the directive method.
 */
TableObjectStepTwoDirective.directiveFactory.$inject = ['$translate'];

export default TableObjectStepTwoDirective;